// src/modules/Roles/components/modals/DeleteModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';

export interface DeleteModalRef {
  /**
   * Abre la modal de confirmación de eliminación.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   */
  open: (onConfirm: (replaceRoleId: number | string) => void) => void;
}

interface DeleteModalProps {
  roles: { id: number | string; name: string }[];
  roleToDelete: number | string;
}

export const DeleteModal = forwardRef<DeleteModalRef, DeleteModalProps>(({ roles = [], roleToDelete }, ref) => {
  const { t } = useTranslation('rolesModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<(replaceRoleId: number | string) => void>(() => {});
  const [replaceRoleId, setReplaceRoleId] = useState<string>('');

  useImperativeHandle(ref, () => ({
    open: (onConfirm: (replaceRoleId: number | string) => void) => {
      confirmRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = () => {
    if (!replaceRoleId) return;
    confirmRef.current(replaceRoleId);
    confirmRef.current = () => {};
    modalRef.current?.closeModal();
  };

  // Opciones para el combobox (excluyendo el rol a eliminar)
  const options = roles.filter((r) => r.id !== roleToDelete).map((r) => ({ label: r.name, value: String(r.id) }));

  // Seleccionar automáticamente si solo hay una opción
  useEffect(() => {
    if (options.length === 1) {
      setReplaceRoleId(options[0].value);
    } else {
      setReplaceRoleId('');
    }
  }, [options.length]);

  return (
    <ModalCenter ref={modalRef} title={t('deleteModal.title')} type="default" width="default">
      <div className="space-y-4">
        <p className="text-sm">{t('deleteModal.message')}</p>
        <Alert variant="default" className="text-sm">
          <div className="flex items-center gap-4">
            <FontAwesomeIcon icon={faTriangleExclamation} className="w-4 h-4" />
            <AlertDescription>{t('deleteModal.warning')}</AlertDescription>
          </div>
        </Alert>
        <div>
          <label className="block text-sm mb-1 font-medium">{t('deleteModal.moveUsers')}</label>
          <Combobox
            options={options}
            placeholder={t('deleteModal.selectReplacement')}
            value={replaceRoleId}
            setValue={setReplaceRoleId}
            className="w-full"
            dataTestId="replace-role-combobox"
          />
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('deleteModal.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm} disabled={!replaceRoleId}>
            {t('deleteModal.delete')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

DeleteModal.displayName = 'DeleteModal';
